export const SMALL_ACCOUNT_BUYING_POWER_THRESHOLD = 2500;
export const SMALL_ACCOUNT_REJECTION_MESSAGE = "Not available for small accounts";
export const SMALL_ACCOUNT_TOOLTIP = "Disabled for accounts under $2,500 due to capital inefficiency";

const ALLOWED_SMALL_ACCOUNT_STRATEGIES = new Set(["tactical_spread", "tactical_spreads"]);
const DISABLED_SMALL_ACCOUNT_STRATEGIES = new Set(["wheel", "regime_spread", "regime_spreads", "earnings"]);

// Wheel-specific capital thresholds
export const WHEEL_MINIMUM_CAPITAL = 5000;
export const WHEEL_WARNING_CAPITAL = 10000;

// Earnings-specific capital threshold
export const EARNINGS_MINIMUM_CAPITAL = 10000;
export const EARNINGS_PREVIEW_ONLY = true; // MVP: live execution disabled until model validated

export const WHEEL_MINIMUM_MESSAGE =
    "Wheel strategy requires at least $5,000 in effective capital. " +
    "Selling a cash-secured put means you may be assigned 100 shares — " +
    "capital needed equals the underlying price × 100 (e.g. a $60 stock " +
    "requires $6,000). " +
    "Consider Tactical Spread strategy instead.";

export const WHEEL_WARNING_MESSAGE =
    "Your effective capital is below $10,000. Wheel strategy works, but " +
    "assignment of 100 shares could occupy a significant portion of your " +
    "account — actual exposure depends on the underlying price × 100. " +
    "Size positions conservatively and stick to lower-priced underlyings.";

export const EARNINGS_MINIMUM_MESSAGE =
    "Earnings strategy requires at least $10,000 in buying power. " +
    "Debit spreads around earnings events carry IV crush risk that demands " +
    "sufficient capital to weather adverse moves and hold multiple setups. " +
    "Consider Tactical Spread strategy instead.";

/**
 * Returns min(buyingPower, portfolioValue) when both are positive finite numbers.
 * Falls back to buyingPower alone when portfolioValue is absent or zero.
 * Prevents margin-inflated buying power from masking true account size.
 */
function effectiveWheelCapital(buyingPower, portfolioValue) {
    const bp = Number(buyingPower);
    if (!Number.isFinite(bp)) return null;
    const pv = Number(portfolioValue);
    if (Number.isFinite(pv) && pv > 0) return Math.min(bp, pv);
    return bp;
}

export function isSmallAccountMode(buyingPower) {
    const numeric = Number(buyingPower);
    return Number.isFinite(numeric) && numeric < SMALL_ACCOUNT_BUYING_POWER_THRESHOLD;
}

export function isStrategyAllowedForBuyingPower(strategy, buyingPower, portfolioValue) {
    const numeric = Number(buyingPower);
    if (!Number.isFinite(numeric)) return true;
    const s = String(strategy || "").trim().toLowerCase();
    if (s === "wheel") {
        const effective = effectiveWheelCapital(buyingPower, portfolioValue);
        return effective !== null && effective >= WHEEL_MINIMUM_CAPITAL;
    }
    if (s === "earnings") {
        return numeric >= EARNINGS_MINIMUM_CAPITAL;
    }
    if (numeric < SMALL_ACCOUNT_BUYING_POWER_THRESHOLD) {
        return ALLOWED_SMALL_ACCOUNT_STRATEGIES.has(s);
    }
    return true;
}

export function isStrategyDisabledForSmallAccount(strategy) {
    return DISABLED_SMALL_ACCOUNT_STRATEGIES.has(String(strategy || "").trim().toLowerCase());
}

/**
 * Returns the tooltip/message to show when a strategy is blocked.
 * Returns null if the strategy is allowed.
 */
export function getStrategyBlockMessage(strategy, buyingPower, portfolioValue) {
    const numeric = Number(buyingPower);
    if (!Number.isFinite(numeric)) return null;
    const s = String(strategy || "").trim().toLowerCase();
    if (s === "wheel") {
        const effective = effectiveWheelCapital(buyingPower, portfolioValue);
        if (effective !== null && effective < WHEEL_MINIMUM_CAPITAL) return WHEEL_MINIMUM_MESSAGE;
    }
    if (s === "earnings" && numeric < EARNINGS_MINIMUM_CAPITAL) {
        return EARNINGS_MINIMUM_MESSAGE;
    }
    if (numeric < SMALL_ACCOUNT_BUYING_POWER_THRESHOLD && !ALLOWED_SMALL_ACCOUNT_STRATEGIES.has(s)) {
        return SMALL_ACCOUNT_REJECTION_MESSAGE;
    }
    return null;
}

/**
 * Returns a non-blocking advisory warning for wheel when effective capital is in
 * the $5,000–$10,000 range. Returns null when no warning applies.
 */
export function getWheelCapitalWarning(buyingPower, portfolioValue) {
    const effective = effectiveWheelCapital(buyingPower, portfolioValue);
    if (effective === null) return null;
    if (effective >= WHEEL_MINIMUM_CAPITAL && effective < WHEEL_WARNING_CAPITAL) {
        return WHEEL_WARNING_MESSAGE;
    }
    return null;
}
